import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IApiContentResponseModel,
  BenefitDto,
  BenefitFiltersDto,
  GetMeBenefitsV1Params,
} from '@/services/platform/api/model';
import { BenefitsScreenPropertiesModel } from '@/services/platform/api/model';
import { useBenefits } from '@/services/platform/data/profile/benefits';
import { useApp } from '@/lib/core/providers/appProvider';
import { useClientTranslation } from '@/lib/core';
import { useLocaleStore } from '@/lib/core/stores';
import { Icon } from '@/components/ui/icon';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { BenefitsFilterSheet } from '@/components/shared/sheets/benefits-filter';
import { BenefitsSearchOverlay } from '@/components/shared/profile/benefits/search';

interface BenefitsPageProps {
  content: IApiContentResponseModel;
}

export function BenefitsPage({ content }: BenefitsPageProps) {
  const navigate = useNavigate();
  const translation = useClientTranslation();
  const { memberTheme, membership } = useApp();

  const properties = content.properties as BenefitsScreenPropertiesModel | undefined;

  const [activeGroup, setActiveGroup] = useState<string>('all');
  const [activeCategories, setActiveCategories] = useState<string[]>([]);
  const [activeLocations, setActiveLocations] = useState<string[]>([]);
  const [appliedCategories, setAppliedCategories] = useState<string[]>([]);
  const [appliedLocations, setAppliedLocations] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);

  const queryParams = useMemo<GetMeBenefitsV1Params | undefined>(() => {
    const p: GetMeBenefitsV1Params = {};
    if (appliedCategories.length > 0) p.Category = appliedCategories.join(',');
    if (appliedLocations.length > 0) p.Location = appliedLocations.join(',');
    return Object.keys(p).length > 0 ? p : undefined;
  }, [appliedCategories, appliedLocations]);

  const locale = useLocaleStore(state => state.locale);
  const { data, isLoading } = useBenefits(queryParams, locale);

  const benefits: BenefitDto[] = (data?.status === 200 ? data.data?.benefits : undefined) ?? [];
  const filters: BenefitFiltersDto = (data?.status === 200 ? data.data?.filters : undefined) ?? {};

  const filteredBenefits = useMemo(() => {
    if (activeGroup === 'all') return benefits;
    return benefits.filter(b => b.group?.id === activeGroup);
  }, [benefits, activeGroup]);

  const activeFilterCount = appliedCategories.length + appliedLocations.length;

  const pageTitle = properties?.pageName ?? translation('App.Labels.MemberBenefits');
  const subtitle = properties?.subtitle;

  const selectedGroupName =
    activeGroup === 'all'
      ? translation('App.Labels.AllBenefits')
      : (filters.groups?.find(g => g.id === activeGroup)?.name ?? translation('App.Labels.AllBenefits'));

  return (
    <>
      <div className="flex min-h-dvh flex-col">
        {/* Header — themed background (dark/green/beige) */}
        <div style={{ background: memberTheme.cardBg }}>
          {/* Top bar */}
          <div
            className="flex shrink-0 items-center justify-between px-2"
            style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
          >
            <div className="flex w-22 items-center">
              <button
                type="button"
                aria-label={translation('App.Labels.GoBack')}
                className="flex size-11 cursor-pointer items-center justify-center"
                onClick={() => navigate(-1)}
              >
                <Icon
                  name="long-arrow-up-left"
                  width={24}
                  height={24}
                  strokeWidth={2}
                  style={{ color: memberTheme.contentColor }}
                />
              </button>
            </div>

            <p
              className="font-pp-neue-montreal shrink-0 text-[14px] leading-none font-normal tracking-[0.5px] uppercase"
              style={{ color: memberTheme.contentColor }}
            >
              {pageTitle}
            </p>

            <div className="flex w-22 justify-end">
              <button
                type="button"
                aria-label={translation('App.Labels.BenefitSearchHint')}
                className="flex size-11 cursor-pointer items-center justify-center"
                onClick={() => setIsSearchOpen(true)}
              >
                <Icon name="search" width={24} height={24} style={{ color: memberTheme.contentColor }} />
              </button>
            </div>
          </div>

          {/* Copy section */}
          <div className="flex flex-col items-center gap-6 px-4 pt-4 pb-12 text-center">
            <div className="flex w-full flex-col items-center">
              {membership?.type && (
                <span
                  className="font-didot w-full text-[36px] leading-8.5 italic"
                  style={{ color: memberTheme.cardAccent, letterSpacing: '-1.44px' }}
                >
                  {membership.type}
                </span>
              )}
              <span
                className="font-pp-neue-montreal w-full text-[36px] leading-tight font-semibold tracking-[0.5px] uppercase"
                style={{ color: memberTheme.contentColor }}
              >
                {translation('App.Labels.Benefits')}
              </span>
            </div>
            {subtitle && (
              <p
                className="font-pp-neue-montreal text-[12px] leading-normal font-normal tracking-[0.5px] uppercase"
                style={{ color: memberTheme.contentColor }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Benefits area — white background */}
        <div className="relative flex flex-1 flex-col bg-white">
          {/* Filter bar — sticky */}
          <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between border-b-[0.5px] border-[#d1d1d1] bg-[rgba(255,255,255,0.9)] px-4 py-1 backdrop-blur-sm">
            <button
              type="button"
              className="flex w-45 items-center justify-between py-2 pr-4"
              onClick={() => setIsGroupDropdownOpen(v => !v)}
            >
              <span className="font-pp-neue-montreal text-[12px] font-semibold tracking-[0.5px] text-[#1a1a1a] uppercase">
                {selectedGroupName}
              </span>
              <Icon
                name="nav-arrow-down"
                width={16}
                height={16}
                className={`text-[#1a1a1a] transition-transform duration-200 ${isGroupDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            <button
              type="button"
              aria-label={translation('App.Labels.FilterTitle')}
              className="flex items-center gap-2 py-2"
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFilterCount > 0 && (
                <span className="font-pp-neue-montreal text-[12px] font-normal tracking-[0.5px] text-[#1a1a1a] uppercase">
                  {translation('App.Labels.FilterActiveCount', { count: activeFilterCount })}
                </span>
              )}
              <Icon name="filter-list" width={20} height={20} className="text-[#1a1a1a]" />
            </button>

            {/* Group dropdown */}
            {isGroupDropdownOpen && (filters.groups?.length ?? 0) > 0 && (
              <div className="border-jncquoi-neutral-200 bg-jncquoi-neutral-50 absolute top-full left-2 z-10 w-45 border-[0.5px]">
                <GroupOption
                  label={translation('App.Labels.AllBenefits')}
                  selected={activeGroup === 'all'}
                  onClick={() => {
                    setActiveGroup('all');
                    setIsGroupDropdownOpen(false);
                  }}
                />
                {filters.groups!.map(group => (
                  <GroupOption
                    key={group.id}
                    label={group.name ?? ''}
                    selected={activeGroup === group.id}
                    onClick={() => {
                      setActiveGroup(group.id ?? 'all');
                      setIsGroupDropdownOpen(false);
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Benefits list */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <SpinLoading wrapperClassName="min-h-40" />
            ) : filteredBenefits.length === 0 ? (
              <p className="font-pp-neue-montreal p-4 pt-12 text-center text-[12px] tracking-[0.5px] text-[#848484] uppercase">
                {translation('App.Labels.BenefitSearchNoResults')}
              </p>
            ) : (
              <BenefitList benefits={filteredBenefits} />
            )}
          </div>
        </div>
      </div>

      {/* Backdrop for group dropdown */}
      {isGroupDropdownOpen && (
        <div className="fixed inset-0 z-9" onClick={() => setIsGroupDropdownOpen(false)} aria-hidden="true" />
      )}

      <BenefitsFilterSheet
        open={isFilterOpen}
        onOpenChange={setIsFilterOpen}
        filters={filters}
        activeCategories={activeCategories}
        activeLocations={activeLocations}
        onApply={(categories, locations) => {
          setActiveCategories(categories);
          setActiveLocations(locations);
          setAppliedCategories(categories);
          setAppliedLocations(locations);
        }}
        onClear={() => {
          setActiveCategories([]);
          setActiveLocations([]);
          setAppliedCategories([]);
          setAppliedLocations([]);
        }}
      />

      <BenefitsSearchOverlay open={isSearchOpen} onClose={() => setIsSearchOpen(false)} filters={filters} />
    </>
  );
}

function GroupOption({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-center justify-between py-3 pr-4 pl-2 text-left"
      onClick={onClick}
    >
      <span
        className={`font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#1a1a1a] uppercase ${selected ? 'font-semibold' : 'font-normal'}`}
      >
        {label}
      </span>
      {selected && <Icon name="check" width={16} height={16} className="text-[#1a1a1a]" />}
    </button>
  );
}

function BenefitList({ benefits }: { benefits: BenefitDto[] }) {
  const groups: { id: string; name: string; items: BenefitDto[] }[] = [];
  for (const b of benefits) {
    const gid = b.group?.id ?? '';
    const existing = groups.find(g => g.id === gid);
    if (existing) {
      existing.items.push(b);
    } else {
      groups.push({ id: gid, name: b.group?.name ?? '', items: [b] });
    }
  }

  return (
    <div className="px-4 pb-22">
      {groups.map(group => (
        <div key={group.id}>
          {group.name && (
            <p className="font-pp-neue-montreal pt-6 pb-2 text-[12px] leading-7.25 font-normal tracking-[0.5px] text-[#1a1a1a] uppercase">
              {group.name}
            </p>
          )}
          {group.items.map(benefit => (
            <BenefitRow key={benefit.id} benefit={benefit} />
          ))}
        </div>
      ))}
    </div>
  );
}

function BenefitRow({ benefit }: { benefit: BenefitDto }) {
  return (
    <div className="flex items-start gap-3 border-b-[0.5px] border-[#dfdfdf] py-4">
      <div className="flex flex-1 flex-col gap-2">
        <p className="font-pp-neue-montreal text-[14px] leading-tight font-semibold tracking-[0.5px] text-[#1a1a1a]">
          {benefit.title}
        </p>
        {benefit.description && (
          <p className="font-pp-neue-montreal text-[14px] leading-normal font-normal tracking-[0.5px] text-[#666]">
            {benefit.description}
          </p>
        )}
      </div>
    </div>
  );
}
